import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetClientMarketInstrumentQueryResponse, GetClientMarketInstrumentPathParams, GetClientMarketInstrument404 } from "../types/GetClientMarketInstrument";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetClientMarketInstrumentClient = typeof client<GetClientMarketInstrumentQueryResponse, GetClientMarketInstrument404, never>;
type GetClientMarketInstrument = {
    data: GetClientMarketInstrumentQueryResponse;
    error: GetClientMarketInstrument404;
    request: never;
    pathParams: GetClientMarketInstrumentPathParams;
    queryParams: never;
    headerParams: never;
    response: GetClientMarketInstrumentQueryResponse;
    client: {
        parameters: Partial<Parameters<GetClientMarketInstrumentClient>[0]>;
        return: Awaited<ReturnType<GetClientMarketInstrumentClient>>;
    };
};
export const getClientMarketInstrumentQueryKey = (clientCode: GetClientMarketInstrumentPathParams["clientCode"], marketCode: GetClientMarketInstrumentPathParams["marketCode"], symbol: GetClientMarketInstrumentPathParams["symbol"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments/:symbol", params: { clientCode: clientCode, marketCode: marketCode, symbol: symbol } }] as const;
export type GetClientMarketInstrumentQueryKey = ReturnType<typeof getClientMarketInstrumentQueryKey>;
export function getClientMarketInstrumentQueryOptions(clientCode: GetClientMarketInstrumentPathParams["clientCode"], marketCode: GetClientMarketInstrumentPathParams["marketCode"], symbol: GetClientMarketInstrumentPathParams["symbol"], options: GetClientMarketInstrument["client"]["parameters"] = {}) {
    const queryKey = getClientMarketInstrumentQueryKey(clientCode, marketCode, symbol);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetClientMarketInstrument["data"], GetClientMarketInstrument["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments/${symbol}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single instrument by symbol for a given market and client
 * @summary Get
 * @link /api/clients/:clientCode/markets/:marketCode/instruments/:symbol
 */
export function useGetClientMarketInstrument<TData = GetClientMarketInstrument["response"], TQueryData = GetClientMarketInstrument["response"], TQueryKey extends QueryKey = GetClientMarketInstrumentQueryKey>(clientCode: GetClientMarketInstrumentPathParams["clientCode"], marketCode: GetClientMarketInstrumentPathParams["marketCode"], symbol: GetClientMarketInstrumentPathParams["symbol"], options: {
    query?: Partial<QueryObserverOptions<GetClientMarketInstrument["response"], GetClientMarketInstrument["error"], TData, TQueryData, TQueryKey>>;
    client?: GetClientMarketInstrument["client"]["parameters"];
} = {}): UseQueryResult<TData, GetClientMarketInstrument["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getClientMarketInstrumentQueryKey(clientCode, marketCode, symbol);
    const query = useQuery({
        ...getClientMarketInstrumentQueryOptions(clientCode, marketCode, symbol, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetClientMarketInstrument["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getClientMarketInstrumentSuspenseQueryKey = (clientCode: GetClientMarketInstrumentPathParams["clientCode"], marketCode: GetClientMarketInstrumentPathParams["marketCode"], symbol: GetClientMarketInstrumentPathParams["symbol"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments/:symbol", params: { clientCode: clientCode, marketCode: marketCode, symbol: symbol } }] as const;
export type GetClientMarketInstrumentSuspenseQueryKey = ReturnType<typeof getClientMarketInstrumentSuspenseQueryKey>;
export function getClientMarketInstrumentSuspenseQueryOptions(clientCode: GetClientMarketInstrumentPathParams["clientCode"], marketCode: GetClientMarketInstrumentPathParams["marketCode"], symbol: GetClientMarketInstrumentPathParams["symbol"], options: GetClientMarketInstrument["client"]["parameters"] = {}) {
    const queryKey = getClientMarketInstrumentSuspenseQueryKey(clientCode, marketCode, symbol);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetClientMarketInstrument["data"], GetClientMarketInstrument["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments/${symbol}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single instrument by symbol for a given market and client
 * @summary Get
 * @link /api/clients/:clientCode/markets/:marketCode/instruments/:symbol
 */
export function useGetClientMarketInstrumentSuspense<TData = GetClientMarketInstrument["response"], TQueryKey extends QueryKey = GetClientMarketInstrumentSuspenseQueryKey>(clientCode: GetClientMarketInstrumentPathParams["clientCode"], marketCode: GetClientMarketInstrumentPathParams["marketCode"], symbol: GetClientMarketInstrumentPathParams["symbol"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetClientMarketInstrument["response"], GetClientMarketInstrument["error"], TData, TQueryKey>>;
    client?: GetClientMarketInstrument["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetClientMarketInstrument["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getClientMarketInstrumentSuspenseQueryKey(clientCode, marketCode, symbol);
    const query = useSuspenseQuery({
        ...getClientMarketInstrumentSuspenseQueryOptions(clientCode, marketCode, symbol, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetClientMarketInstrument["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}